import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  CloseOutlined,
  CloudUploadOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import FieldLabel from "components/common/FieldLabel";
import UserAvatar from "components/common/UserAvatar";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  type SecurePerson,
  useFinalizeSecureUploadMutation,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useGetSecureRecipientsQuery,
  usePresignSecureUploadMutation,
} from "features/secureUploads/secureUploads";
import { useViewAs } from "hooks/useViewAs";
import { formatApiError } from "utils/formatApiError";

import { ConfidentialTag } from "./ProjectAccessEditor";
import { fileSize } from "./fileModel";
import { keepAllowed, locationLabel, uploadBlocker } from "./uploadModel";

/** The place a page uploads to; a page without one lets the uploader pick. */
export interface UploadPlace {
  projectId?: number | null;
  projectName?: string | null;
  collectionId?: number | null;
  collectionName?: string | null;
}

interface SecureUploadDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  /** Fixed by the page (Individual Files, a project, a collection). */
  place?: UploadPlace;
  /** The Projects tab: Individual Files by default, or an open project. */
  pickPlace?: boolean;
}

// A select shows nothing for an empty value, so the defaults carry names.
const INDIVIDUAL = "individual";
const PROJECT_ROOT = "root";

/** Upload a file where the page is, shared with people who will see it there. */
export default function SecureUploadDialog({
  open,
  onClose,
  clientId,
  place,
  pickPlace = false,
}: SecureUploadDialogProps) {
  const dispatch = useDispatch();
  const { profile } = useViewAs();
  const staff = profile?.user_type === "internal";
  const input = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pickedProject, setPickedProject] = useState(INDIVIDUAL);
  const [pickedCollection, setPickedCollection] = useState(PROJECT_ROOT);
  const [confidential, setConfidential] = useState(false);
  const [sharedWith, setSharedWith] = useState<number[]>([]);
  const [message, setMessage] = useState("");
  const [silent, setSilent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPickedProject(INDIVIDUAL);
    setPickedCollection(PROJECT_ROOT);
    setConfidential(false);
    setSharedWith([]);
    setMessage("");
    setSilent(false);
    setFailure(null);
  }, [open]);

  const { data: openProjects } = useGetSecureProjectsQuery(
    { client_id: clientId, status: "open", page_size: 200 },
    { skip: !open || !pickPlace },
  );
  const projectId = pickPlace
    ? Number(pickedProject) || null
    : (place?.projectId ?? null);
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !pickPlace || !projectId },
  );
  const collectionId = pickPlace
    ? Number(pickedCollection) || null
    : (place?.collectionId ?? null);
  const location = {
    client_id: clientId,
    project_id: projectId,
    collection_id: collectionId,
  };
  const { data: peopleData, isFetching: loadingPeople } =
    useGetSecureRecipientsQuery({ ...location, confidential }, { skip: !open });
  const people = useMemo(() => peopleData ?? [], [peopleData]);

  // Narrower lists (Confidential, a project) drop whoever they leave out.
  useEffect(() => {
    if (loadingPeople) return;
    setSharedWith((chosen) => {
      const kept = keepAllowed(chosen, people);
      return kept.length === chosen.length ? chosen : kept;
    });
  }, [people, loadingPeople]);

  const [presign] = usePresignSecureUploadMutation();
  const [finalize] = useFinalizeSecureUploadMutation();

  const chosenPeople = useMemo(
    () =>
      sharedWith
        .map((id) => people.find((person) => person.id === id))
        .filter((person): person is SecurePerson => Boolean(person)),
    [sharedWith, people],
  );
  const blocker = uploadBlocker(file, sharedWith);
  const projectName = pickPlace
    ? openProjects?.results.find((p) => p.id === projectId)?.name
    : place?.projectName;
  const collectionName = pickPlace
    ? collections.find((c) => c.id === collectionId)?.name
    : place?.collectionName;

  const upload = async () => {
    if (!file || blocker) return;
    setBusy(true);
    setFailure(null);
    try {
      const contentType = file.type || "application/octet-stream";
      const presigned = await presign({
        ...location,
        file_name: file.name,
        content_type: contentType,
      }).unwrap();
      const put = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: presigned.headers,
        body: file,
      });
      if (!put.ok) throw new Error("The file could not be sent to storage.");
      const saved = await finalize({
        ...location,
        s3_key: presigned.s3_key,
        file_name: presigned.file_name,
        content_type: contentType,
        shared_with: sharedWith,
        is_confidential: confidential,
        message: message.trim(),
        is_silent: staff && silent,
      }).unwrap();
      const notified =
        saved.emails_sent > 0
          ? ` ${saved.emails_sent} ${saved.emails_sent === 1 ? "person was" : "people were"} emailed.`
          : "";
      dispatch(
        pushNotification({
          message: `File uploaded.${notified}`,
          severity: "success",
        }),
      );
      onClose();
    } catch (error) {
      const data = (error as { data?: unknown })?.data;
      setFailure(
        data
          ? formatApiError(data, "The file could not be uploaded.")
          : error instanceof Error
            ? error.message
            : "The file could not be uploaded.",
      );
    } finally {
      setBusy(false);
    }
  };

  const takeFile = (files: FileList | null) => {
    const next = files?.[0];
    if (next) setFile(next);
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <FormHeader
          avatar={<CloudUploadOutlined fontSize="small" />}
          title="Upload New File"
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3, overflowY: "auto", display: "grid", gap: 2.5 }}>
          {pickPlace ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <FieldLabel>Upload To</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={pickedProject}
                  disabled={busy}
                  onChange={(event) => {
                    setPickedProject(event.target.value);
                    setPickedCollection(PROJECT_ROOT);
                  }}
                >
                  <MenuItem value={INDIVIDUAL}>Individual Files</MenuItem>
                  {(openProjects?.results ?? []).map((project) => (
                    <MenuItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {projectId ? (
                <Box>
                  <FieldLabel>Collection</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={pickedCollection}
                    disabled={busy}
                    onChange={(event) =>
                      setPickedCollection(event.target.value)
                    }
                  >
                    <MenuItem value={PROJECT_ROOT}>No collection</MenuItem>
                    {collections.map((collection) => (
                      <MenuItem
                        key={collection.id}
                        value={String(collection.id)}
                      >
                        {collection.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ) : null}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Uploading to <b>{locationLabel(projectName, collectionName)}</b>
            </Typography>
          )}

          <Box>
            <FieldLabel required>File</FieldLabel>
            <input
              ref={input}
              type="file"
              hidden
              onChange={(event) => {
                takeFile(event.target.files);
                event.target.value = "";
              }}
            />
            {file ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <InsertDriveFileOutlined sx={{ color: "text.secondary" }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap variant="body2">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fileSize(file.size)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<CloseOutlined fontSize="small" />}
                  disabled={busy}
                  onClick={() => setFile(null)}
                >
                  Remove
                </Button>
              </Box>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => input.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ")
                    input.current?.click();
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  takeFile(event.dataTransfer.files);
                }}
                sx={{
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  border: "1.5px dashed",
                  borderColor: dragging ? "primary.main" : "divider",
                  borderRadius: 1,
                  bgcolor: dragging ? "action.hover" : "transparent",
                }}
              >
                <CloudUploadOutlined sx={{ color: "text.secondary" }} />
                <Typography variant="body2">
                  Drag a file here or <b>choose one</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Up to 100 MB
                </Typography>
              </Box>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={confidential}
                disabled={busy}
                onChange={(event) => setConfidential(event.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Confidential
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Only people with Confidential Access will see it - and you.
                </Typography>
              </Box>
            }
          />

          <Box>
            <FieldLabel required>Shared With</FieldLabel>
            <Autocomplete<SecurePerson, true>
              multiple
              options={people}
              value={chosenPeople}
              loading={loadingPeople}
              disabled={busy}
              onChange={(_event, value) =>
                setSharedWith(value.map((person) => person.id))
              }
              getOptionLabel={(person) => person.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, person) => (
                <Box component="li" {...props} key={person.id} sx={{ gap: 1 }}>
                  <UserAvatar person={person} size={24} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2">{person.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {person.organization}
                      {person.email ? ` - ${person.email}` : ""}
                    </Typography>
                  </Box>
                  {staff && person.confidential ? <ConfidentialTag /> : null}
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((person, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={person.id}
                    size="small"
                    avatar={<UserAvatar person={person} size={20} />}
                    label={person.name}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={chosenPeople.length ? "" : "Select people"}
                />
              )}
              noOptionsText={
                confidential
                  ? "Nobody here holds Confidential Access"
                  : "Nobody else can see files here"
              }
            />
            <Typography variant="caption" color="text.secondary">
              Only people who will see the file there are listed.
            </Typography>
          </Box>

          <Box>
            <FieldLabel>Message</FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="Add a note for the people you share it with"
              value={message}
              disabled={busy}
              onChange={(event) => setMessage(event.target.value)}
            />
          </Box>

          {staff ? (
            <FormControlLabel
              control={
                <Switch
                  checked={silent}
                  disabled={busy}
                  onChange={(event) => setSilent(event.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Send without notification
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No email goes out; the file still shows as new.
                  </Typography>
                </Box>
              }
            />
          ) : null}

          {failure ? <Alert severity="error">{failure}</Alert> : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
      >
        <Button
          variant="contained"
          size="small"
          onClick={upload}
          disabled={busy || Boolean(blocker)}
          title={blocker ?? undefined}
          sx={FOOTER_BUTTON_SX}
        >
          {busy ? "Uploading..." : "Upload"}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
